import { Alert, Box, Button } from '@mui/material';
import React from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { SETUP_SECTIONS, sectionById } from '../../domain/setup/sections';
import { useTenantScope } from '../../store/selectors';
import { PageHeader, StatusChip } from '../../ui/primitives';
import ActivationPanel from './ActivationPanel';
import ReviewPublishPanel from './ReviewPublishPanel';
import SetupSectionForm from './SetupSectionForm';
import SetupSideNav, { isSectionLocked } from './SetupSideNav';

export default function SetupWizardPage() {
  const { setupId } = useParams();
  const [params, setParams] = useSearchParams();
  const scope = useTenantScope();
  const setup = scope.fundSetups.find((item) => item.id === setupId);
  if (!setup) {
    return (
      <Alert
        severity="info"
        action={
          <Button component={RouterLink} to="/manager/setup">
            Back
          </Button>
        }
      >
        This setup no longer exists. Resetting the demo clears setups in progress.
      </Alert>
    );
  }
  const defaultSection =
    setup.status === 'DRAFT' ? 'trust' : setup.status === 'ACTIVE' ? 'offer' : 'review';
  const requested = sectionById(params.get('step') ?? '');
  const section =
    requested && !isSectionLocked(setup, requested.group)
      ? requested
      : sectionById(defaultSection)!;
  const index = SETUP_SECTIONS.findIndex((item) => item.id === section.id);
  const goTo = (id: string) => {
    setParams({ step: id });
    window.scrollTo({ top: 0 });
  };
  const next = SETUP_SECTIONS[index + 1];
  const previous = SETUP_SECTIONS[index - 1];
  const readOnly =
    (section.group !== 'OFFER' && setup.status !== 'DRAFT') ||
    (section.group === 'OFFER' && setup.status === 'PUBLISHED');

  return (
    <>
      <PageHeader
        eyebrow="Set up a new deal"
        title={String(setup.values['fund.name'])}
        subtitle={`Started by ${setup.createdBy}. Progress saves as you go.`}
        actions={
          <StatusChip
            status={setup.status}
            label={setup.status === 'DRAFT' ? 'Draft' : undefined}
          />
        }
      />
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', lg: '240px minmax(0, 1fr)' },
        }}
      >
        <Box>
          <SetupSideNav setup={setup} current={section.id} onSelect={goTo} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {section.id === 'activate' ? (
            <ActivationPanel setup={setup} onGoTo={goTo} onNext={() => goTo('offer')} />
          ) : section.id === 'review' ? (
            <ReviewPublishPanel setup={setup} onGoTo={goTo} />
          ) : (
            <SetupSectionForm
              key={section.id}
              setup={setup}
              section={section}
              readOnly={readOnly}
              onBack={previous ? () => goTo(previous.id) : undefined}
              onNext={() => next && goTo(next.id)}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
